import clsx from "clsx";
import React, { useRef, useEffect, forwardRef, useState } from "react";
import { HiArrowUp } from "react-icons/hi";

interface ScrollRestoreV2Props {
  children: React.ReactNode;
  id: string;
  className?: string;
  initRestore?: boolean;
  debounceTime?: number;
  showScrollTopButton?: boolean;
}

/**
 * ScrollRestoreV2는 호출하는 페이지에서 ref 선언 후 전달받는 형태로 사용
 * 이미지 lazy loading을 위함
 */
const ScrollRestoreV2 = forwardRef<HTMLDivElement, ScrollRestoreV2Props>(
  (
    {
      children,
      id,
      className,
      initRestore,
      debounceTime = 100,
      showScrollTopButton = false,
    },
    ref
  ) => {
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [scrollPosition, setScrollPosition] = useState(0);
    const handleScroll = () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = setTimeout(() => {
        if (ref && typeof ref === "object" && ref.current) {
          sessionStorage.setItem(id, ref.current.scrollTop.toString());
          setScrollPosition(ref.current.scrollTop);
        }
      }, debounceTime);
    };

    useEffect(() => {
      if (!initRestore) return;

      // 새로고침 시 sessionStorage에서 해당 id 제거
      const handleBeforeUnload = () => {
        sessionStorage.removeItem(id);
      };

      window.addEventListener("beforeunload", handleBeforeUnload);

      const savedScroll = sessionStorage.getItem(id) || "0";
      if (ref && typeof ref === "object" && ref.current) {
        ref.current.scrollTop = parseInt(savedScroll, 10);
      }

      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }, [id, initRestore, ref]);

    useEffect(() => {
      return () => {
        if (debounceTimeout.current) {
          clearTimeout(debounceTimeout.current);
        }
      };
    }, []);

    return (
      <div
        ref={ref}
        onScroll={handleScroll}
        className={clsx(className, {
          "overflow-y-scroll": !className?.includes("overflow-y-scroll"),
        })}
      >
        {children}
        {/* 우측 하단 버튼 */}
        {/* 노출: 스크롤이 0이상일 경우, 클릭시: 스크롤 최상단으로 이동 */}
        {scrollPosition > 0 && showScrollTopButton && (
          <div
            onClick={() => {
              if (ref && typeof ref === "object" && ref.current) {
                ref.current.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="h-12 w-12 opacity-95 hover:opacity-100 transition-opacity duration-300 rounded-full bg-[#1562fc] absolute right-4 bottom-[80px] flex items-center justify-center cursor-pointer z-20"
          >
            <HiArrowUp className="text-white text-2xl" />
          </div>
        )}
      </div>
    );
  }
);

ScrollRestoreV2.displayName = "ScrollRestoreV2";

export default ScrollRestoreV2;
